import { useEffect, useState } from "react";
import api from "../../services/api";

/* ================= BRAND COLORS ================= */
const COLORS = {
  primary: "#0D47A1",
  red: "#D32F2F",
  yellow: "#FBC02D",
  bgCream: "#FFF6EA",
  card: "#FFFFFF",
  border: "#E5E7EB",
  textDark: "#1E1E1E",
  textGrey: "#757575",
  success: "#16A34A",
};

const CashInHand = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState("desktop");

  /* NEW STATES */
  const [showModal, setShowModal] = useState(false);
  const [openingBalance, setOpeningBalance] = useState("");
  const [date, setDate] = useState("");

  useEffect(() => {
    load();

    const updateScreen = () => {
      const w = window.innerWidth;
      if (w < 600) setScreen("mobile");
      else if (w < 1024) setScreen("tablet");
      else setScreen("desktop");
    };

    updateScreen();
    window.addEventListener("resize", updateScreen);

    // auto date
    const today = new Date().toISOString().split("T")[0];
    setDate(today);

    return () => window.removeEventListener("resize", updateScreen);
  }, []);

  const load = async () => {
    try {
      const res = await api.get("/cash/list.php");
      setData(res.data?.success ? res.data.data : null);
    } catch (err) {
      console.error("Cash API Error:", err);
      setData(null);
    }
    setLoading(false);
  };

  /* HANDLE SUBMIT */
  const handleSubmit = async () => {
    if (!openingBalance) {
      alert("Enter opening balance");
      return;
    }

    try {
      await api.post("/cash/add_opening.php", {
        amount: openingBalance,
        date: date,
      });

      setShowModal(false);
      setOpeningBalance("");
      load(); // refresh
    } catch (err) {
      console.error(err);
      alert("Failed to save");
    }
  };

  if (loading) return <div style={{ padding: 20 }}>Loading...</div>;
  if (!data) return <div style={{ padding: 20 }}>Failed to load data</div>;

  const sales = Number(data.sales || 0);
  const purchase = Number(data.purchase || 0);
  const paymentIn = Number(data.payment_in || 0);
  const paymentOut = Number(data.payment_out || 0);
  const expenses = Number(data.expenses || 0);
  const cash = Number(data.cash_in_hand || 0);

  const totalInflow = sales + paymentIn;
  const totalOutflow = purchase + paymentOut + expenses;

  return (
    <div
      style={{
        ...styles.page,
        padding:
          screen === "mobile"
            ? "10px 12px"
            : screen === "tablet"
            ? "10px 20px"
            : "10px 30px",
      }}
    >
      {/* HEADER */}
      <div style={styles.header}>
        <div style={styles.headerRow}>
          <div>
            <h2
              style={{
                ...styles.title,
                fontSize: screen === "mobile" ? 18 : 24,
              }}
            >
              Cash Overview
            </h2>

            <span style={{ ...styles.subtitle, fontSize: 13 }}>
              Monitor your liquidity and cash movements
            </span>
          </div>

          {/* ADD CASH BUTTON */}
          <button
            style={styles.addBtn}
            onClick={() => setShowModal(true)}
          >
            + Add Cash
          </button>
        </div>
      </div>

      {/* BALANCE */}
      <div
        style={{
          ...styles.balanceCard,
          padding: screen === "mobile" ? 18 : 28,
        }}
      >
        <div style={styles.balanceLabel}>Available Cash</div>

        <div
          style={{
            ...styles.balanceValue,
            fontSize: screen === "mobile" ? 22 : 28,
          }}
        >
          ₹ {cash.toLocaleString()}
        </div>
      </div>

      {/* SUMMARY */}
      <div
        style={{
          ...styles.summaryRow,
          gridTemplateColumns:
            screen === "mobile"
              ? "1fr"
              : "repeat(auto-fit, minmax(180px,1fr))",
        }}
      >
        <SummaryCard title="Total Inflow" value={totalInflow} color={COLORS.success} />
        <SummaryCard title="Total Outflow" value={totalOutflow} color={COLORS.red} />
      </div>

      {/* GRID */}
      <div
        style={{
          ...styles.grid,
          gridTemplateColumns:
            screen === "mobile"
              ? "1fr"
              : screen === "tablet"
              ? "repeat(2,1fr)"
              : "repeat(auto-fit, minmax(160px,1fr))",
        }}
      >
        <Card title="Sales" value={sales} color={COLORS.success} />
        <Card title="Payment In" value={paymentIn} color={COLORS.success} />
        <Card title="Purchase" value={purchase} color={COLORS.red} />
        <Card title="Payment Out" value={paymentOut} color={COLORS.red} />
        <Card title="Expenses" value={expenses} color={COLORS.red} />
      </div>

      {/* MODAL */}
      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Add Opening Balance</h3>

            <label style={styles.label}>Amount</label>
            <input
              type="number"
              value={openingBalance}
              onChange={(e) => setOpeningBalance(e.target.value)}
              style={styles.input}
            />

            <label style={styles.label}>Date</label>
            <input
              type="date"
              value={date}
              disabled
              style={styles.input}
            />

            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button style={styles.saveBtn} onClick={handleSubmit}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* COMPONENTS */

const SummaryCard = ({ title, value, color }) => (
  <div style={styles.summaryCard}>
    <div style={styles.summaryTitle}>{title}</div>
    <div style={{ ...styles.summaryValue, color }}>
      ₹ {Number(value).toLocaleString()}
    </div>
  </div>
);

const Card = ({ title, value, color }) => (
  <div style={styles.card}>
    <div style={styles.cardTitle}>{title}</div>
    <div style={{ ...styles.cardValue, color }}>
      ₹ {Number(value).toLocaleString()}
    </div>
  </div>
);

/* STYLES */

const styles = {
  page: {
    background: COLORS.bgCream,
    minHeight: "100vh",
  },

  header: {
    marginBottom: 20,
  },

  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
  },

  addBtn: {
    background: COLORS.primary,
    color: "#fff",
    border: "none",
    padding: "10px 16px",
    borderRadius: 8,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  /* ✅ FIXED MODAL */
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16, // important for mobile spacing
    zIndex: 999,
  },

  modal: {
    background: "#fff",
    borderRadius: 14,
    width: "100%",
    maxWidth: 420,
    padding: 20,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    maxHeight: "90vh",
    overflowY: "auto",
  },

  modalTitle: {
    marginBottom: 5,
    fontSize: 18,
    fontWeight: 600,
  },

  label: {
    fontSize: 13,
    color: COLORS.textGrey,
  },

  /* ✅ FIXED INPUT */
  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 14,
    boxSizing: "border-box", // CRITICAL FIX
  },

  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 10,
    flexWrap: "wrap",
  },

  cancelBtn: {
    padding: "10px 16px",
    border: "1px solid #ccc",
    background: "#fff",
    borderRadius: 8,
    cursor: "pointer",
  },

  saveBtn: {
    padding: "10px 16px",
    background: COLORS.primary,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
  },

  title: {
    fontWeight: 700,
    color: COLORS.textDark,
  },

  subtitle: {
    color: COLORS.textGrey,
  },

  balanceCard: {
    background: COLORS.primary,
    color: "#fff",
    borderRadius: 16,
    marginBottom: 20,
    boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
  },

  balanceLabel: {
    fontSize: 13,
    opacity: 0.85,
    marginBottom: 6,
  },

  balanceValue: {
    fontWeight: 700,
  },

  summaryRow: {
    display: "grid",
    gap: 15,
    marginBottom: 20,
  },

  summaryCard: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 16,
  },

  summaryTitle: {
    fontSize: 12,
    color: COLORS.textGrey,
  },

  summaryValue: {
    fontSize: 18,
    fontWeight: 700,
  },

  grid: {
    display: "grid",
    gap: 15,
  },

  card: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 14,
  },

  cardTitle: {
    fontSize: 12,
    color: COLORS.textGrey,
  },

  cardValue: {
    fontSize: 16,
    fontWeight: 600,
  },
};

export default CashInHand;